import React, { useState, useCallback, Component } from 'react';
import { jsPDF } from 'jspdf';
import { documentationContent } from '../data/documentationContent';
import { 
  FileText, Database, Lock, Server, Rocket, Layout, Code, Shield, BookOpen,
  Download, X, Check, Printer, FileCode, GitBranch, Zap, Package, Users,
  CreditCard, Bell, Globe, Workflow, Truck, FileCheck, Calculator, Smartphone,
  Building, MessageSquare, BarChart3, Settings, ChevronRight, AlertTriangle,
  ArrowLeft
} from 'lucide-react';
import { Link } from 'react-router-dom';

const iconMap = {
  FileText, Database, Lock, Server, Rocket, Layout, Code, Shield, BookOpen,
  Workflow: GitBranch, Zap, Package, Users, CreditCard, Bell, Globe,
  Truck, FileCheck, Calculator, Smartphone, Building, MessageSquare,
  BarChart3, Settings
};

// React Error Boundary for catching rendering errors
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          padding: '3rem',
          textAlign: 'center',
          background: 'var(--bg-card)',
          borderRadius: '16px',
          border: '1px solid rgba(244, 63, 94, 0.3)',
          margin: '2rem'
        }}>
          <AlertTriangle size={48} style={{ color: '#f43f5e', marginBottom: '1rem' }} />
          <h2 style={{ color: '#f8fafc', fontFamily: 'Sora, sans-serif', marginBottom: '0.5rem' }}>
            Something went wrong
          </h2>
          <p style={{ color: '#94a3b8', marginBottom: '1.5rem' }}>
            {this.state.error?.message || 'An unexpected error occurred while rendering content.'}
          </p>
          <button
            onClick={() => this.setState({ hasError: false, error: null })}
            style={{
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            Try Again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Error Boundary for Content Rendering
const ContentErrorFallback = ({ error, sectionTitle }) => (
  <div style={{
    padding: '2rem',
    background: 'rgba(244, 63, 94, 0.1)',
    border: '1px solid rgba(244, 63, 94, 0.3)',
    borderRadius: '12px',
    margin: '1rem 0'
  }}>
    <h4 style={{ color: '#f43f5e', marginBottom: '0.5rem', fontFamily: 'Sora, sans-serif' }}>
      Content Rendering Error
    </h4>
    <p style={{ color: '#94a3b8', fontSize: '0.875rem' }}>
      Unable to render {sectionTitle || 'this section'}. Error: {error?.message || 'Unknown error'}
    </p>
  </div>
);

// Safe Markdown Renderer with error handling
const renderMarkdown = (content, sectionTitle = 'content') => {
  if (!content) return null;
  
  try {
    return renderMarkdownContent(content);
  } catch (error) {
    console.error(`Error rendering markdown for ${sectionTitle}:`, error);
    return <ContentErrorFallback error={error} sectionTitle={sectionTitle} />;
  }
};

// Markdown Renderer (internal)
const renderMarkdownContent = (content) => {
  const lines = content.split('\n');
  const elements = [];
  let inCodeBlock = false;
  let codeContent = [];
  let inTable = false;
  let tableRows = [];
  let inList = false;
  let listItems = [];
  let listType = 'ul';
  
  const processLine = (line, index) => {
    if (line.startsWith('```')) {
      if (inCodeBlock) {
        elements.push(
          <pre key={`code-${index}`}>
            <code>{codeContent.join('\n')}</code>
          </pre>
        );
        codeContent = [];
        inCodeBlock = false;
      } else {
        inCodeBlock = true;
      }
      return;
    }
    
    if (inCodeBlock) {
      codeContent.push(line);
      return;
    }
    
    const trimmedLine = line.trim();
    if (trimmedLine.startsWith('|') && trimmedLine.endsWith('|')) {
      if (!inTable) {
        inTable = true;
        tableRows = [];
      }
      if (!trimmedLine.includes('---')) {
        const cells = trimmedLine.split('|').filter(cell => cell.trim() !== '');
        tableRows.push(cells.map(cell => cell.trim()));
      }
      return;
    } else if (inTable && trimmedLine !== '') {
      const headers = tableRows[0];
      const rows = tableRows.slice(1);
      elements.push(
        <table key={`table-${index}`}>
          <thead>
            <tr>{headers.map((h, i) => <th key={i}>{formatInline(h)}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri}>
                {row.map((cell, ci) => <td key={ci}>{formatInline(cell)}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      );
      inTable = false;
      tableRows = [];
    }
    
    const ulMatch = line.match(/^[•\-\*]\s+(.+)/);
    const olMatch = line.match(/^\d+\.\s+(.+)/);
    const checkMatch = line.match(/^- \[([ x])\]\s+(.+)/);
    
    if (checkMatch || ulMatch || olMatch) {
      if (!inList) {
        inList = true;
        listItems = [];
        listType = olMatch ? 'ol' : 'ul';
      }
      
      if (checkMatch) {
        const checked = checkMatch[1] === 'x';
        listItems.push(
          <li key={`li-${index}`} style={{ listStyle: 'none', marginLeft: '-1.5rem', display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
            <span style={{ color: checked ? 'var(--accent-lime)' : 'var(--text-dim)', marginTop: '0.125rem' }}>
              {checked ? '✓' : '○'}
            </span>
            <span>{formatInline(checkMatch[2])}</span>
          </li>
        );
      } else {
        listItems.push(
          <li key={`li-${index}`}>{formatInline(ulMatch ? ulMatch[1] : olMatch[1])}</li>
        );
      }
      return;
    } else if (inList && line.trim() === '') {
      const ListTag = listType;
      elements.push(<ListTag key={`list-${index}`}>{listItems}</ListTag>);
      inList = false;
      listItems = [];
    }
    
    if (line.startsWith('####')) {
      elements.push(<h4 key={index}>{formatInline(line.replace(/^####\s*/, ''))}</h4>);
    } else if (line.startsWith('###')) {
      elements.push(<h3 key={index}>{formatInline(line.replace(/^###\s*/, ''))}</h3>);
    } else if (line.startsWith('##')) {
      elements.push(<h2 key={index}>{formatInline(line.replace(/^##\s*/, ''))}</h2>);
    } else if (line.trim() !== '' && !inTable) {
      elements.push(<p key={index}>{formatInline(line)}</p>);
    }
  };
  
  lines.forEach((line, index) => processLine(line, index));
  
  if (inTable && tableRows.length > 0) {
    const headers = tableRows[0];
    const rows = tableRows.slice(1);
    elements.push(
      <table key="table-end">
        <thead>
          <tr>{headers.map((h, i) => <th key={i}>{formatInline(h)}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri}>
              {row.map((cell, ci) => <td key={ci}>{formatInline(cell)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  
  if (inList && listItems.length > 0) {
    const ListTag = listType;
    elements.push(<ListTag key="list-end">{listItems}</ListTag>);
  }
  
  return elements;
};

const formatInline = (text) => {
  if (!text) return text;
  const parts = [];
  let remaining = text;
  let keyCounter = 0;
  
  while (remaining) {
    const codeMatch = remaining.match(/`([^`]+)`/);
    const boldMatch = remaining.match(/\*\*([^*]+)\*\*/);
    
    if (!codeMatch && !boldMatch) {
      parts.push(remaining);
      break;
    }
    
    const codeIndex = codeMatch ? remaining.indexOf(codeMatch[0]) : Infinity;
    const boldIndex = boldMatch ? remaining.indexOf(boldMatch[0]) : Infinity;
    
    if (codeIndex < boldIndex) {
      if (codeIndex > 0) parts.push(remaining.substring(0, codeIndex));
      parts.push(<code key={keyCounter++}>{codeMatch[1]}</code>);
      remaining = remaining.substring(codeIndex + codeMatch[0].length);
    } else {
      if (boldIndex > 0) parts.push(remaining.substring(0, boldIndex));
      parts.push(<strong key={keyCounter++}>{boldMatch[1]}</strong>);
      remaining = remaining.substring(boldIndex + boldMatch[0].length);
    }
  }
  
  return parts;
};

// HTML Generator
const generateHtmlContent = () => {
  let html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Bolt Logistics SaaS - System Documentation v2.0</title>
  <link href="https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500&family=Manrope:wght@400;500;600;700&family=Sora:wght@400;600;700&display=swap" rel="stylesheet">
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Manrope', sans-serif; line-height: 1.7; color: #1e293b; max-width: 900px; margin: 0 auto; padding: 48px 24px; }
    h1 { font-family: 'Sora', sans-serif; font-size: 36px; color: #0f172a; margin-bottom: 8px; font-weight: 700; }
    h2 { font-family: 'Sora', sans-serif; font-size: 24px; color: #3b82f6; margin: 40px 0 16px; padding-bottom: 12px; border-bottom: 2px solid #3b82f6; font-weight: 600; }
    h3 { font-family: 'Sora', sans-serif; font-size: 18px; color: #0f172a; margin: 28px 0 12px; font-weight: 600; }
    h4 { font-size: 16px; color: #334155; margin: 20px 0 10px; font-weight: 600; }
    p { margin-bottom: 14px; color: #475569; }
    strong { color: #0f172a; font-weight: 600; }
    table { width: 100%; border-collapse: collapse; margin: 20px 0; font-size: 14px; border-radius: 8px; overflow: hidden; }
    th { background: #f1f5f9; padding: 12px 16px; text-align: left; font-weight: 600; color: #0f172a; border: 1px solid #e2e8f0; }
    td { padding: 10px 16px; border: 1px solid #e2e8f0; color: #475569; }
    pre { background: #0f172a; color: #e2e8f0; padding: 20px; border-radius: 12px; overflow-x: auto; margin: 20px 0; font-size: 13px; }
    code { font-family: 'JetBrains Mono', monospace; }
    p code, li code { background: #e0f2fe; color: #0369a1; padding: 2px 8px; border-radius: 4px; font-size: 14px; }
    ul, ol { margin: 14px 0; padding-left: 28px; }
    li { margin-bottom: 8px; color: #475569; }
    .subtitle { color: #64748b; font-size: 20px; margin-bottom: 8px; }
    .meta { color: #94a3b8; font-size: 14px; margin-bottom: 40px; padding-bottom: 20px; border-bottom: 1px solid #e2e8f0; }
    .section { page-break-inside: avoid; margin-bottom: 40px; }
    @media print { body { padding: 24px; font-size: 12px; } h1 { font-size: 28px; } h2 { font-size: 20px; } }
  </style>
</head>
<body>
  <h1>${documentationContent.title}</h1>
  <p class="subtitle">${documentationContent.subtitle}</p>
  <p class="meta">Version ${documentationContent.version} • ${documentationContent.date}</p>
`;

  documentationContent.sections.forEach(section => {
    html += `<div class="section"><h2>${section.title}</h2>`;
    if (section.content) html += convertMarkdownToHtml(section.content);
    if (section.subsections) {
      section.subsections.forEach(sub => {
        html += `<h3>${sub.title}</h3>`;
        html += convertMarkdownToHtml(sub.content);
      });
    }
    html += `</div>`;
  });

  html += `</body></html>`;
  return html;
};

const convertMarkdownToHtml = (content) => {
  if (!content) return '';
  let html = content;
  html = html.replace(/```(\w*)\n([\s\S]*?)```/g, '<pre><code>$2</code></pre>');
  html = html.replace(/\|(.+)\|\n\|[-| ]+\|\n((?:\|.+\|\n?)+)/g, (match, header, body) => {
    const headers = header.split('|').filter(h => h.trim()).map(h => `<th>${h.trim()}</th>`).join('');
    const rows = body.trim().split('\n').map(row => {
      const cells = row.split('|').filter(c => c.trim()).map(c => `<td>${c.trim()}</td>`).join('');
      return `<tr>${cells}</tr>`;
    }).join('');
    return `<table><thead><tr>${headers}</tr></thead><tbody>${rows}</tbody></table>`;
  });
  html = html.replace(/^####\s+(.+)$/gm, '<h4>$1</h4>');
  html = html.replace(/^###\s+(.+)$/gm, '<h3>$1</h3>');
  html = html.replace(/^##\s+(.+)$/gm, '<h3>$1</h3>');
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>');
  html = html.replace(/^[•\-\*]\s+(.+)$/gm, '<li>$1</li>');
  html = html.replace(/(<li>.+<\/li>\n?)+/g, '<ul>$&</ul>');
  html = html.replace(/- \[ \]\s+(.+)/g, '<li>○ $1</li>');
  html = html.replace(/- \[x\]\s+(.+)/g, '<li>✓ $1</li>');
  html = html.replace(/^(?!<[a-z]|$)(.+)$/gm, '<p>$1</p>');
  return html;
};

export default function Documentation() {
  const [activeSection, setActiveSection] = useState('executive-summary');
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (message) => {
    setToast(message);
    setTimeout(() => setToast(null), 3000);
  };

  const downloadPdf = useCallback(async () => {
    setIsLoading(true);
    setShowDownloadModal(false);
    
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      const contentWidth = pageWidth - (margin * 2);
      let yPosition = margin;
      
      const addText = (text, fontSize, isBold = false, color = [30, 41, 59]) => {
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
        pdf.setTextColor(...color);
        const lines = pdf.splitTextToSize(text, contentWidth);
        const lineHeight = fontSize * 0.5;
        lines.forEach(line => {
          if (yPosition + lineHeight > pageHeight - margin) {
            pdf.addPage();
            yPosition = margin;
          }
          pdf.text(line, margin, yPosition);
          yPosition += lineHeight;
        });
        return lineHeight * lines.length;
      };
      
      pdf.setFontSize(28);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(15, 23, 42);
      pdf.text(documentationContent.title, margin, yPosition);
      yPosition += 12;
      
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(100, 116, 139);
      pdf.text(documentationContent.subtitle, margin, yPosition);
      yPosition += 8;
      
      pdf.setFontSize(10);
      pdf.text(`Version ${documentationContent.version} • ${documentationContent.date}`, margin, yPosition);
      yPosition += 15;
      
      documentationContent.sections.forEach(section => {
        if (yPosition > pageHeight - 40) {
          pdf.addPage();
          yPosition = margin;
        }
        
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(59, 130, 246);
        pdf.text(section.title, margin, yPosition);
        yPosition += 4;
        pdf.setDrawColor(59, 130, 246);
        pdf.setLineWidth(0.5);
        pdf.line(margin, yPosition, margin + contentWidth, yPosition);
        yPosition += 10;
        
        if (section.content) {
          const cleanContent = section.content
            .replace(/\*\*/g, '').replace(/`/g, '')
            .replace(/```[\s\S]*?```/g, '[Code block - see HTML version]')
            .replace(/\|[^\n]+\|/g, '').replace(/[-•]\s/g, '• ');
          addText(cleanContent, 10, false, [71, 85, 105]);
          yPosition += 5;
        }
        
        if (section.subsections) {
          section.subsections.forEach(sub => {
            if (yPosition > pageHeight - 30) {
              pdf.addPage();
              yPosition = margin;
            }
            pdf.setFontSize(12);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(30, 41, 59);
            pdf.text(sub.title, margin, yPosition);
            yPosition += 8;
            const cleanSubContent = sub.content
              .replace(/\*\*/g, '').replace(/`/g, '')
              .replace(/```[\s\S]*?```/g, '[Code block - see HTML version]')
              .replace(/\|[^\n]+\|/g, '[Table - see HTML version]')
              .replace(/[-•]\s/g, '• ');
            addText(cleanSubContent, 9, false, [71, 85, 105]);
            yPosition += 5;
          });
        }
        yPosition += 10;
      });
      
      pdf.save('Bolt-Nexus-System-Documentation-v2.pdf');
      setIsLoading(false);
      setTimeout(() => showToast('PDF downloaded successfully!'), 100);
    } catch (error) {
      console.error('PDF generation error:', error);
      setIsLoading(false);
      setTimeout(() => showToast('Error generating PDF'), 100);
    }
  }, []);

  const downloadHtml = useCallback(() => {
    setShowDownloadModal(false);
    const htmlContent = generateHtmlContent();
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Bolt-Nexus-System-Documentation-v2.html';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    showToast('HTML downloaded successfully!');
  }, []);

  const currentSection = documentationContent.sections.find(s => 
    s.id === activeSection || s.subsections?.some(sub => sub.id === activeSection)
  );
  const currentSubsection = currentSection?.subsections?.find(sub => sub.id === activeSection);

  return (
    <div className="docs-page">
      <div className="ambient-glow" />
      
      {/* Header */}
      <header className="doc-header">
        <div className="header-inner">
          <div className="logo-group">
            <Link to="/dashboard" className="back-to-app" data-testid="back-to-app">
              <ArrowLeft size={20} />
            </Link>
            <div className="logo-mark">B</div>
            <div className="logo-text">
              <span className="logo-title">Bolt Nexus</span>
              <span className="logo-subtitle">System Documentation</span>
            </div>
          </div>
          
          <div className="header-actions">
            <button className="btn btn-primary" onClick={() => setShowDownloadModal(true)} data-testid="download-btn">
              <Download size={16} />
              Download
            </button>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="main-container docs-main">
        {/* Sidebar */}
        <aside className="sidebar" data-testid="docs-sidebar">
          <div className="sidebar-section">
            <div className="sidebar-label">Documentation</div>
            {documentationContent.sections.map(section => {
              const IconComponent = iconMap[section.icon] || FileText;
              const isActive = activeSection === section.id || 
                section.subsections?.some(sub => sub.id === activeSection);
              
              return (
                <div key={section.id}>
                  <div 
                    className={`nav-item ${isActive && !currentSubsection ? 'active' : ''}`}
                    onClick={() => setActiveSection(section.id)}
                    data-testid={`nav-${section.id}`}
                  >
                    <IconComponent size={18} />
                    <span>{section.title.replace(/^\d+\.\s*/, '')}</span>
                  </div>
                  
                  {section.subsections && isActive && (
                    <div>
                      {section.subsections.map(sub => (
                        <div 
                          key={sub.id}
                          className={`nav-item nav-sub-item ${activeSection === sub.id ? 'active' : ''}`}
                          onClick={() => setActiveSection(sub.id)}
                          data-testid={`nav-${sub.id}`}
                        >
                          <ChevronRight size={14} />
                          <span>{sub.title.replace(/^\d+\.\d+\s*/, '')}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </aside>

        {/* Content */}
        <div className="content-area" data-testid="docs-content-area">
          <ErrorBoundary>
            <div className="content-wrapper" key={activeSection}>
              {currentSubsection ? (
                <>
                  <div className="section-header">
                    <div className="section-badge">
                      <FileText size={12} />
                      Subsection
                    </div>
                    <h1 className="section-title">{currentSubsection.title}</h1>
                  </div>
                  <div className="markdown-body">
                    {renderMarkdown(currentSubsection.content, currentSubsection.title)}
                  </div>
                </>
              ) : currentSection ? (
                <>
                  <div className="section-header">
                    <div className="section-badge">
                      {iconMap[currentSection.icon] && 
                        React.createElement(iconMap[currentSection.icon], { size: 12 })}
                      Section {documentationContent.sections.indexOf(currentSection) + 1}
                    </div>
                    <h1 className="section-title">{currentSection.title}</h1>
                    {currentSection.description && (
                      <p className="section-description">{currentSection.description}</p>
                    )}
                  </div>
                  
                  {currentSection.content && (
                    <div className="markdown-body">
                      {renderMarkdown(currentSection.content, currentSection.title)}
                    </div>
                  )}
                  
                  {currentSection.subsections && (
                    <div style={{ marginTop: '2rem' }}>
                      <h2 style={{ 
                        fontFamily: 'Sora, sans-serif', 
                        fontSize: '1.25rem', 
                        color: 'var(--text-main)',
                        marginBottom: '1rem'
                      }}>
                        In This Section
                      </h2>
                      <div className="feature-grid">
                        {currentSection.subsections.map((sub, idx) => (
                          <div 
                            key={sub.id} 
                            className="feature-card"
                            onClick={() => setActiveSection(sub.id)}
                            data-testid={`feature-card-${sub.id}`}
                          >
                            <div className={`feature-icon ${['blue', 'cyan', 'lime', 'amber', 'rose'][idx % 5]}`}>
                              {String(idx + 1).padStart(2, '0')}
                            </div>
                            <div className="feature-title">{sub.title}</div>
                            <div className="feature-desc">
                              {sub.content?.substring(0, 120).replace(/[#*`|]/g, '').trim()}...
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : null}
            </div>
          </ErrorBoundary>
        </div>
      </main>

      {/* Download Modal */}
      {showDownloadModal && (
        <div className="modal-backdrop" onClick={() => setShowDownloadModal(false)}>
          <div className="modal-panel" onClick={e => e.stopPropagation()} data-testid="download-modal">
            <div className="modal-header">
              <h3 className="modal-title">Download Documentation</h3>
              <button className="modal-close" onClick={() => setShowDownloadModal(false)}>
                <X size={18} />
              </button>
            </div>
            
            <div className="download-options">
              <div className="download-option" onClick={downloadPdf} data-testid="download-pdf-option">
                <div className="download-icon pdf">
                  <Printer size={22} color="white" />
                </div>
                <div className="download-info">
                  <h4>PDF Document</h4>
                  <p>Best for printing and offline reading</p>
                </div>
              </div>
              
              <div className="download-option" onClick={downloadHtml} data-testid="download-html-option">
                <div className="download-icon html">
                  <FileCode size={22} color="white" />
                </div>
                <div className="download-info">
                  <h4>HTML Document</h4>
                  <p>Complete formatting with tables and code</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="loading-overlay">
          <div className="loading-spinner" />
          <p className="loading-text">Generating PDF...</p>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="toast" data-testid="toast">
          <Check size={18} />
          <span>{toast}</span>
        </div>
      )}
    </div>
  );
}
